import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import {
  closeModal,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";
import {
  ExpenseCategoryList,
  ExpenseTransactionCreate,
  ExpenseTransactionDelete,
  ExpenseTransactionList,
  ExpenseTransactionUpdate,
} from "../../api/HostelExpenses";
import Header from "./HostelHeader";
import Nav from "../master/NavBar";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Footer from "../main/footer";
import { GetHostelList } from "../../api/HostelService";
import WelcomeBar from "./WelcomeBar";
import { StaffList } from "../../api/Staff";
import { BankList } from "../../api/BankService";

export default function Expenses() {
  const hasAccess = usePrivateLink();

  /* =========================================================
     STATES
  ========================================================= */

  const [loading, setLoading] = useState(false);

  const [expenses, setExpenses] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [banks, setBanks] = useState([]);

  const [transactions, setTransactions] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
    per_page: 15,
    links: [],
  });

  const [totalSum, setTotalSum] = useState(0);
  const [modalData, setModalData] = useState(null);

  const [type] = useState("Hostel");

  const [error, setError] = useState({});
  const [expense_id, setExpenseId] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");

  /* =========================================================
     FILTERS
  ========================================================= */

  const [searchExpense_id, setSearchExpenseId] = useState("");
  const [staff_id, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);

  const [hostel_id, setHostelId] = useState(
    localStorage.getItem("hostel_id") || "",
  );

  /* =========================================================
     ADD FORM
  ========================================================= */

  const [addForm, setAddForm] = useState({
    hostel_id: "",
    expense_id: "",
    staff_id: "",
    amount: "",
    date: "",
    bank_id: "",
    description: "",
    status: "1",
  });

  /* =========================================================
     EDIT FORM
  ========================================================= */

  const [editForm, setEditForm] = useState({
    id: "",
    hostel_id: "",
    expense_id: "",
    staff_id: "",
    amount: "",
    date: "",
    type: "Cash",
    description: "",
    status: "1",
  });

  /* =========================================================
     LOCAL STORAGE HOSTEL
  ========================================================= */

  useEffect(() => {
    const handleStorageChange = () => {
      setHostelId(localStorage.getItem("hostel_id") || "");
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  /* =========================================================
     ERROR HELPER
  ========================================================= */

  const getErrorMessage = (field) => {
    const value = error?.[field];

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return value || "";
  };

  /* =========================================================
     BANK LIST
  ========================================================= */

  const getBusinessBanks = async () => {
    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(Array.isArray(response?.data?.data) ? response.data.data : []);
      } else {
        setBanks([]);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setBanks([]);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    }
  };

  /* =========================================================
     HOSTEL LIST
  ========================================================= */

  const getHostel = async () => {
    try {
      let addUrl = "";

      if (hostel_id) {
        addUrl += `&hostel_id=${hostel_id}`;
      }

      const response = await GetHostelList(addUrl);

      if (response?.data?.status) {
        /*
          API may return:

          response.data.data = [
             ...
          ]

          OR

          response.data.data = {
             data: [...]
          }

          Normalize both.
        */

        const hostelData = response?.data?.data;

        if (Array.isArray(hostelData)) {
          setHostels(hostelData);
        } else if (Array.isArray(hostelData?.data)) {
          setHostels(hostelData.data);
        } else {
          setHostels([]);
        }
      } else {
        setHostels([]);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setHostels([]);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    }
  };

  /* =========================================================
     EXPENSE CATEGORY
  ========================================================= */

  const getExpenseCategory = async () => {
    try {
      const response = await ExpenseCategoryList();

      if (response?.data?.status) {
        const categoryData = response?.data?.data;

        if (Array.isArray(categoryData)) {
          setExpenses(categoryData);
        } else if (Array.isArray(categoryData?.data)) {
          setExpenses(categoryData.data);
        } else {
          setExpenses([]);
        }
      } else {
        setExpenses([]);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setExpenses([]);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    }
  };

  /* =========================================================
     STAFF LIST
  ========================================================= */

  const getStaffList = async () => {
    try {
      const response = await StaffList();

      if (response?.data?.status) {
        /*
          Existing API structure:

          response.data.data = [...]
        */

        const staffData = response?.data?.data;

        if (Array.isArray(staffData)) {
          setStaffs(staffData);
        } else if (Array.isArray(staffData?.data)) {
          setStaffs(staffData.data);
        } else {
          setStaffs([]);
        }
      } else {
        setStaffs([]);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setStaffs([]);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    }
  };

  /* =========================================================
     TRANSACTIONS
  ========================================================= */

  const getTransactions = async () => {
    setLoading(true);

    let addUrl = `&type=${type}`;

    if (page) {
      addUrl += `&page=${page}`;
    }

    if (searchExpense_id) {
      addUrl += `&expense_id=${searchExpense_id}`;
    }

    /* FIX:
       Previously staff_id was incorrectly sent as expense_id
    */
    if (staff_id) {
      addUrl += `&staff_id=${staff_id}`;
    }

    if (date) {
      addUrl += `&date=${date}`;
    }

    if (startDate) {
      addUrl += `&start_time=${startDate}`;
    }

    if (endDate) {
      addUrl += `&end_time=${endDate}`;
    }

    if (hostel_id) {
      addUrl += `&hostel_id=${hostel_id}`;
    }

    try {
      const response = await ExpenseTransactionList(addUrl);

      if (response?.data?.status) {
        setTransactions(
          response?.data?.data || {
            data: [],
            current_page: 1,
            last_page: 1,
            total: 0,
            from: 0,
            to: 0,
            per_page: 15,
            links: [],
          },
        );

        setTotalSum(response?.data?.total_sum || 0);
      } else {
        setTransactions({
          data: [],
          current_page: 1,
          last_page: 1,
          total: 0,
          from: 0,
          to: 0,
          per_page: 15,
          links: [],
        });

        setTotalSum(0);

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setTransactions({
        data: [],
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
        per_page: 15,
        links: [],
      });

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL DATA
  ========================================================= */

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      try {
        await Promise.all([
          getBusinessBanks(),
          getStaffList(),
          getExpenseCategory(),
          getHostel(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  /* =========================================================
     FETCH TRANSACTIONS
  ========================================================= */

  useEffect(() => {
    getTransactions();
  }, [searchExpense_id, staff_id, date, page, startDate, endDate, hostel_id]);

  /* =========================================================
     ADD EXPENSE
  ========================================================= */

  const AddExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseTransactionCreate(formData);

      if (response?.data?.status) {
        setError({});

        closeModal("AddCategory");

        setAddForm({
          hostel_id: "",
          expense_id: "",
          staff_id: "",
          amount: "",
          date: "",
          bank_id: "",
          description: "",
          status: "1",
        });

        setExpenseId("");
        setSelectedAccount("");

        getTransactions();

        ToastMain.fire({
          icon: "success",
          title: "Expense created successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      if (err?.response?.data?.errors) {
        setError(err.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     UPDATE EXPENSE
  ========================================================= */

  const UpdateExpenses = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await ExpenseTransactionUpdate(formData);

      if (response?.data?.status) {
        setError({});

        closeModal("Edit");

        getTransactions();

        ToastMain.fire({
          icon: "success",
          title: "Expense updated successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      if (err?.response?.data?.errors) {
        setError(err.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const response = await ExpenseTransactionDelete(id);

      if (response?.data?.status) {
        getTransactions();

        ToastMain.fire({
          icon: "success",
          title: "Expense deleted successfully!",
        });
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.response?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     OPEN EDIT
  ========================================================= */

  const openEditModal = (item) => {
    setModalData(item);

    setEditForm({
      id: item?.id || "",
      hostel_id: item?.hostel_id || "",
      expense_id: item?.expense_id || "",
      staff_id: item?.staff_id || "",
      amount: item?.amount || "",
      date: item?.date || "",
      type: item?.payment_type || "Cash",
      description: item?.description || "",
      status: item?.status || "1",
    });

    setExpenseId(item?.expense_id || "");
    setError({});
  };

  /* =========================================================
     RESET FILTERS
  ========================================================= */

  const resetFilters = () => {
    setSearchExpenseId("");
    setStaffId("");
    setDate("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  /* =========================================================
     FORM HELPERS
  ========================================================= */

  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "expense_id") {
      setExpenseId(value);
    }
  };

  /* =========================================================
     FORMAT
  ========================================================= */

  const pagination = transactions || {};

  const currentPage = pagination?.current_page || 1;
  const lastPage = pagination?.last_page || 1;

  const hasFilters =
    Boolean(searchExpense_id) ||
    Boolean(staff_id) ||
    Boolean(date) ||
    Boolean(startDate) ||
    Boolean(endDate);

  const formatAmount = (amount) => {
    const number = Number(amount || 0);

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCategoryName = (item) => {
    return item?.expense?.title || "Uncategorized";
  };

  const getStaffName = (item) => {
    return item?.staff?.name || "";
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <>
      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-4">
                <WelcomeBar />

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "rgba(105,108,255,.12)",
                          color: "var(--bs-primary)",
                        }}
                      >
                        <i className="ri-wallet-3-line fs-4" />
                      </div>

                      <div>
                        <h4 className="mb-1 fw-bold">Hostel Expenses</h4>

                        <small className="text-muted">
                          Manage and monitor hostel expense transactions
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {hasAccess("/hostel/expenses") && (
                      <Link
                        to="/hostel/expenses"
                        className="btn btn-primary d-flex align-items-center gap-2"
                      >
                        <i className="ri-exchange-dollar-line" />
                        Transactions
                      </Link>
                    )}

                    {hasAccess("/hostel/expense/category") && (
                      <Link
                        to="/hostel/expense/category"
                        className="btn btn-label-primary d-flex align-items-center gap-2"
                      >
                        <i className="ri-price-tag-3-line" />
                        Categories
                      </Link>
                    )}
                  </div>
                </div>

                {/* =================================================
                    SUMMARY CARDS
                ================================================= */}

                <div className="row g-3 mb-4">
                  {/* TOTAL */}

                  <div className="col-sm-6 col-xl-4">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(105,108,255,.13), rgba(105,108,255,.03))",
                      }}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted">Total Expenses</small>

                            <h3 className="fw-bold mt-2 mb-1">
                              ₹ {formatAmount(totalSum)}
                            </h3>

                            <small className="text-muted">
                              Based on current filters
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "46px",
                              height: "46px",
                              background: "rgba(105,108,255,.14)",
                              color: "var(--bs-primary)",
                            }}
                          >
                            <i className="ri-money-rupee-circle-line fs-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* TRANSACTIONS */}

                  <div className="col-sm-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted">Transactions</small>

                            <h3 className="fw-bold mt-2 mb-1">
                              {pagination?.total || 0}
                            </h3>

                            <small className="text-muted">Total records</small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "46px",
                              height: "46px",
                              background: "rgba(40,199,111,.12)",
                              color: "#28c76f",
                            }}
                          >
                            <i className="ri-file-list-3-line fs-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CURRENT VIEW */}

                  <div className="col-sm-12 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Current View</small>

                            <h5 className="fw-bold mt-2 mb-1">
                              {hasFilters
                                ? "Filtered Results"
                                : "All Transactions"}
                            </h5>

                            <small className="text-muted">
                              {hasFilters
                                ? "Filters are currently active"
                                : "Showing all available expenses"}
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "46px",
                              height: "46px",
                              background: "rgba(255,159,67,.12)",
                              color: "#ff9f43",
                            }}
                          >
                            <i className="ri-filter-3-line fs-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    FILTER CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: "38px",
                            height: "38px",
                            background: "rgba(105,108,255,.10)",
                            color: "var(--bs-primary)",
                          }}
                        >
                          <i className="ri-filter-3-line" />
                        </div>

                        <div>
                          <h6 className="mb-0 fw-bold">Filter Transactions</h6>

                          <small className="text-muted">
                            Narrow down expenses by category, staff or date
                          </small>
                        </div>
                      </div>

                      {hasFilters && (
                        <span className="badge bg-label-primary mt-2 mt-md-0">
                          Filters Applied
                        </span>
                      )}
                    </div>

                    <div className="row g-3">
                      {/* CATEGORY */}

                      <div className="col-md-6 col-xl-3">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={searchExpense_id}
                            onChange={(e) => {
                              setSearchExpenseId(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Categories</option>

                            {Array.isArray(expenses) &&
                              expenses.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.title}
                                </option>
                              ))}
                          </select>

                          <label>Expense Category</label>
                        </div>
                      </div>

                      {/* STAFF */}

                      <div className="col-md-6 col-xl-3">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={staff_id}
                            onChange={(e) => {
                              setStaffId(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Staff</option>

                            {Array.isArray(staffs) &&
                              staffs.map((staff) => (
                                <option key={staff.id} value={staff.id}>
                                  {staff.name}
                                </option>
                              ))}
                          </select>

                          <label>Staff</label>
                        </div>
                      </div>

                      {/* START DATE */}

                      <div className="col-md-6 col-xl-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            onChange={(e) => {
                              setStartDate(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>Start Date</label>
                        </div>
                      </div>

                      {/* END DATE */}

                      <div className="col-md-6 col-xl-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={endDate}
                            min={startDate || undefined}
                            onChange={(e) => {
                              setEndDate(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>End Date</label>
                        </div>
                      </div>

                      {/* RESET */}

                      <div className="col-md-12 col-xl-2 d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-label-danger w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={resetFilters}
                          disabled={!hasFilters}
                        >
                          <i className="ri-refresh-line" />
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    TRANSACTION TABLE
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-bottom py-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div>
                        <h5 className="mb-1 fw-bold">Expense Transactions</h5>

                        <small className="text-muted">
                          Complete record of hostel expenses
                        </small>
                      </div>

                      <div className="d-flex align-items-center gap-3">
                        <div className="text-end">
                          <small className="text-muted d-block">Total</small>

                          <span className="fw-bold text-success">
                            ₹ {formatAmount(totalSum)}
                          </span>
                        </div>

                        {hasAccess("/hostel/expenses/add") && (
                          <button
                            className="btn btn-primary d-flex align-items-center gap-2"
                            data-bs-toggle="modal"
                            data-bs-target="#AddCategory"
                            onClick={() => {
                              setError({});
                              setExpenseId("");
                            }}
                          >
                            <i className="ri-add-line" />
                            Add Expense
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr className="text-nowrap">
                            <th className="ps-4">#</th>
                            <th>Expense</th>
                            <th>Category</th>
                            <th>Date</th>
                            <th>Payment</th>
                            <th>Remark / Staff</th>
                            <th>Status</th>
                            <th className="text-end pe-4">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {Array.isArray(transactions?.data) &&
                          transactions.data.length > 0 ? (
                            transactions.data.map((item, index) => {
                              const isComplete = String(item?.status) === "1";

                              const staffName = getStaffName(item);

                              return (
                                <tr key={item.id}>
                                  {/* NUMBER */}

                                  <td className="ps-4">
                                    <span className="text-muted fw-medium">
                                      {(currentPage - 1) *
                                        (pagination?.per_page || 15) +
                                        index +
                                        1}
                                    </span>
                                  </td>

                                  {/* AMOUNT */}

                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        className="rounded-3 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                          background: "rgba(255,159,67,.10)",
                                          color: "#ff9f43",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <i className="ri-wallet-3-line" />
                                      </div>

                                      <div>
                                        <div className="fw-bold">
                                          ₹ {formatAmount(item.amount)}
                                        </div>

                                        <small className="text-muted">
                                          Expense #{item.id}
                                        </small>
                                      </div>
                                    </div>
                                  </td>

                                  {/* CATEGORY */}

                                  <td>
                                    <span className="badge bg-label-primary px-3 py-2">
                                      {getCategoryName(item)}
                                    </span>
                                  </td>

                                  {/* DATE */}

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="ri-calendar-line text-muted" />

                                      <span>{item?.date || "-"}</span>
                                    </div>
                                  </td>

                                  {/* PAYMENT */}

                                  <td>
                                    <span
                                      className={`badge px-3 py-2 ${
                                        item?.payment_type === "Online"
                                          ? "bg-label-info"
                                          : "bg-label-warning"
                                      }`}
                                    >
                                      <i
                                        className={`me-1 ${
                                          item?.payment_type === "Online"
                                            ? "ri-bank-card-line"
                                            : "ri-money-rupee-circle-line"
                                        }`}
                                      />

                                      {item?.payment_type || "-"}
                                    </span>
                                  </td>

                                  {/* REMARK */}

                                  <td
                                    style={{
                                      minWidth: "200px",
                                    }}
                                  >
                                    {staffName && (
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <div
                                          className="rounded-circle d-flex align-items-center justify-content-center"
                                          style={{
                                            width: "26px",
                                            height: "26px",
                                            background: "rgba(105,108,255,.10)",
                                            color: "var(--bs-primary)",
                                          }}
                                        >
                                          <i className="ri-user-line small" />
                                        </div>

                                        <span className="fw-semibold small">
                                          {staffName}
                                        </span>
                                      </div>
                                    )}

                                    <small
                                      className="text-muted d-block text-truncate"
                                      style={{
                                        maxWidth: "230px",
                                      }}
                                      title={item?.description || ""}
                                    >
                                      {item?.description || "No remark"}
                                    </small>
                                  </td>

                                  {/* STATUS */}

                                  <td>
                                    <span
                                      className={`badge px-3 py-2 ${
                                        isComplete
                                          ? "bg-label-success"
                                          : "bg-label-warning"
                                      }`}
                                    >
                                      <i
                                        className={`me-1 ${
                                          isComplete
                                            ? "ri-checkbox-circle-line"
                                            : "ri-time-line"
                                        }`}
                                      />

                                      {isComplete ? "Complete" : "Pending"}
                                    </span>
                                  </td>

                                  {/* ACTION */}

                                  <td className="text-end pe-4">
                                    <div className="d-flex justify-content-end gap-2">
                                      {hasAccess("/hostel/expenses/update") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-icon btn-label-primary"
                                          data-bs-toggle="modal"
                                          data-bs-target="#Edit"
                                          title="Edit Expense"
                                          onClick={() => openEditModal(item)}
                                        >
                                          <i className="ri-edit-line" />
                                        </button>
                                      )}

                                      {hasAccess("/hostel/expenses/delete") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-icon btn-label-danger"
                                          title="Delete Expense"
                                          onClick={() => {
                                            Swal.fire({
                                              title: "Delete Expense?",
                                              text: "This expense will be permanently deleted.",
                                              icon: "warning",
                                              showCancelButton: true,
                                              confirmButtonColor: "#d33",
                                              cancelButtonColor: "#6c757d",
                                              confirmButtonText:
                                                "Yes, delete it",
                                              cancelButtonText: "Cancel",
                                            }).then((result) => {
                                              if (result.isConfirmed) {
                                                handleDelete(item.id);
                                              }
                                            });
                                          }}
                                        >
                                          <i className="ri-delete-bin-line" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-5">
                                <div className="py-4">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                                    style={{
                                      width: "70px",
                                      height: "70px",
                                      background: "rgba(108,117,125,.08)",
                                    }}
                                  >
                                    <i className="ri-inbox-line fs-1 text-muted" />
                                  </div>

                                  <h6 className="fw-bold mb-1">
                                    No transactions found
                                  </h6>

                                  <p className="text-muted mb-3">
                                    There are no expense transactions matching
                                    your current filters.
                                  </p>

                                  {hasFilters && (
                                    <button
                                      className="btn btn-label-primary"
                                      onClick={resetFilters}
                                    >
                                      <i className="ri-refresh-line me-1" />
                                      Clear Filters
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* PAGINATION */}

                    {pagination?.total > 0 && (
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-4 border-top">
                        <div>
                          <small className="text-muted">
                            Showing <strong>{pagination?.from || 0}</strong> to{" "}
                            <strong>{pagination?.to || 0}</strong> of{" "}
                            <strong>{pagination?.total || 0}</strong> entries
                          </small>
                        </div>

                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            {/* PREVIOUS */}

                            <li
                              className={`page-item ${
                                currentPage <= 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                disabled={currentPage <= 1}
                                onClick={() => setPage(currentPage - 1)}
                              >
                                <i className="ri-arrow-left-s-line" />
                              </button>
                            </li>

                            {/* PAGE NUMBERS */}

                            {Array.from(
                              {
                                length: Math.min(lastPage, 7),
                              },
                              (_, i) => {
                                let pageNumber = i + 1;

                                if (lastPage > 7) {
                                  if (currentPage <= 4) {
                                    pageNumber = i + 1;
                                  } else if (currentPage >= lastPage - 3) {
                                    pageNumber = lastPage - 6 + i;
                                  } else {
                                    pageNumber = currentPage - 3 + i;
                                  }
                                }

                                return (
                                  <li
                                    key={pageNumber}
                                    className={`page-item ${
                                      currentPage === pageNumber ? "active" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => setPage(pageNumber)}
                                    >
                                      {pageNumber}
                                    </button>
                                  </li>
                                );
                              },
                            )}

                            {/* NEXT */}

                            <li
                              className={`page-item ${
                                currentPage >= lastPage ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                disabled={currentPage >= lastPage}
                                onClick={() => setPage(currentPage + 1)}
                              >
                                <i className="ri-arrow-right-s-line" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          ADD EXPENSE MODAL
      ========================================================= */}

      <div
        className="modal fade"
        id="AddCategory"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={AddExpenses}>
              {/* HEADER */}

              <div className="modal-header border-0 px-4 py-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "42px",
                      height: "42px",
                      background: "rgba(105,108,255,.12)",
                      color: "var(--bs-primary)",
                    }}
                  >
                    <i className="ri-add-circle-line fs-4" />
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-0">
                      Add New Expense
                    </h5>

                    <small className="text-muted">
                      Record a new hostel expense transaction
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() => setError({})}
                />
              </div>

              {/* BODY */}

              <div className="modal-body px-4">
                <input type="hidden" name="expense_type" value={type} />

                <div className="row g-3">
                  {/* HOSTEL */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="hostel_id"
                        value={addForm.hostel_id}
                        onChange={(e) =>
                          handleAddChange("hostel_id", e.target.value)
                        }
                        required
                      >
                        <option value="">Select Hostel</option>

                        {Array.isArray(hostels) &&
                          hostels.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                      </select>

                      <label>Hostel Name</label>
                    </div>

                    {getErrorMessage("hostel_id") && (
                      <small className="text-danger">
                        {getErrorMessage("hostel_id")}
                      </small>
                    )}
                  </div>

                  {/* CATEGORY */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="expense_id"
                        value={addForm.expense_id}
                        onChange={(e) => {
                          handleAddChange("expense_id", e.target.value);

                          setExpenseId(e.target.value);
                        }}
                        required
                      >
                        <option value="">Select Expense Category</option>

                        {Array.isArray(expenses) &&
                          expenses.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.title}
                            </option>
                          ))}
                      </select>

                      <label>Expense Category</label>
                    </div>

                    {getErrorMessage("expense_id") && (
                      <small className="text-danger">
                        {getErrorMessage("expense_id")}
                      </small>
                    )}
                  </div>

                  {/* STAFF */}

                  {(expense_id === "1" || expense_id === "2") && (
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          name="staff_id"
                          value={addForm.staff_id}
                          onChange={(e) =>
                            handleAddChange("staff_id", e.target.value)
                          }
                        >
                          <option value="">Select Staff</option>

                          {Array.isArray(staffs) &&
                            staffs.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}
                              </option>
                            ))}
                        </select>

                        <label>Staff</label>
                      </div>
                    </div>
                  )}

                  {/* AMOUNT */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        step="0.01"
                        min="0"
                        value={addForm.amount}
                        onChange={(e) =>
                          handleAddChange("amount", e.target.value)
                        }
                        required
                      />

                      <label>Amount</label>
                    </div>

                    {getErrorMessage("amount") && (
                      <small className="text-danger">
                        {getErrorMessage("amount")}
                      </small>
                    )}
                  </div>

                  {/* DATE */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={addForm.date}
                        onChange={(e) =>
                          handleAddChange("date", e.target.value)
                        }
                        required
                      />

                      <label>Date</label>
                    </div>
                  </div>

                  {/* ACCOUNT */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="bank_id"
                        value={selectedAccount}
                        onChange={(e) => {
                          setSelectedAccount(e.target.value);

                          handleAddChange("bank_id", e.target.value);
                        }}
                        required
                      >
                        <option value="">Select Account</option>

                        {Array.isArray(banks) &&
                          banks.map((item) => (
                            <option key={item?.id} value={item?.id}>
                              {item?.account_no} ({item?.owner_name})
                            </option>
                          ))}
                      </select>

                      <label>Payment Account</label>
                    </div>
                  </div>

                  {/* STATUS */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="status"
                        value={addForm.status}
                        onChange={(e) =>
                          handleAddChange("status", e.target.value)
                        }
                        required
                      >
                        <option value="1">Complete</option>

                        <option value="2">Pending</option>
                      </select>

                      <label>Payment Status</label>
                    </div>
                  </div>

                  {/* DESCRIPTION */}

                  <div className="col-12">
                    <div className="form-floating form-floating-outline">
                      <textarea
                        className="form-control"
                        name="description"
                        placeholder="Remark"
                        style={{
                          minHeight: "100px",
                        }}
                        value={addForm.description}
                        onChange={(e) =>
                          handleAddChange("description", e.target.value)
                        }
                      />

                      <label>Remark / Description</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer border-0 px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                  onClick={() => setError({})}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4 d-flex align-items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line" />
                      Save Expense
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          EDIT EXPENSE MODAL
      ========================================================= */}

      <div className="modal fade" id="Edit" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={UpdateExpenses}>
              {/* HEADER */}

              <div className="modal-header border-0 px-4 py-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "42px",
                      height: "42px",
                      background: "rgba(105,108,255,.12)",
                      color: "var(--bs-primary)",
                    }}
                  >
                    <i className="ri-edit-2-line fs-4" />
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-0">Update Expense</h5>

                    <small className="text-muted">
                      Modify expense transaction details
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() => setError({})}
                />
              </div>

              {/* BODY */}

              <div className="modal-body px-4">
                <input type="hidden" name="id" value={editForm.id} />

                <div className="row g-3">
                  {/* HOSTEL */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="hostel_id"
                        value={editForm.hostel_id}
                        onChange={(e) =>
                          handleEditChange("hostel_id", e.target.value)
                        }
                        required
                      >
                        <option value="">Select Hostel</option>

                        {Array.isArray(hostels) &&
                          hostels.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                      </select>

                      <label>Hostel Name</label>
                    </div>
                  </div>

                  {/* CATEGORY */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="expense_id"
                        value={editForm.expense_id}
                        onChange={(e) =>
                          handleEditChange("expense_id", e.target.value)
                        }
                        required
                      >
                        <option value="">Select Expense Category</option>

                        {Array.isArray(expenses) &&
                          expenses.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.title}
                            </option>
                          ))}
                      </select>

                      <label>Expense Category</label>
                    </div>
                  </div>

                  {/* STAFF */}

                  {(String(editForm.expense_id) === "1" ||
                    String(editForm.expense_id) === "2") && (
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          name="staff_id"
                          value={editForm.staff_id}
                          onChange={(e) =>
                            handleEditChange("staff_id", e.target.value)
                          }
                        >
                          <option value="">Select Staff</option>

                          {Array.isArray(staffs) &&
                            staffs.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}
                              </option>
                            ))}
                        </select>

                        <label>Staff</label>
                      </div>
                    </div>
                  )}

                  {/* AMOUNT */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        step="0.01"
                        min="0"
                        value={editForm.amount}
                        onChange={(e) =>
                          handleEditChange("amount", e.target.value)
                        }
                        required
                      />

                      <label>Amount</label>
                    </div>
                  </div>

                  {/* DATE */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={editForm.date}
                        onChange={(e) =>
                          handleEditChange("date", e.target.value)
                        }
                        required
                      />

                      <label>Date</label>
                    </div>
                  </div>

                  {/* PAYMENT TYPE */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="type"
                        value={editForm.type}
                        onChange={(e) =>
                          handleEditChange("type", e.target.value)
                        }
                        required
                      >
                        <option value="Cash">Cash</option>

                        <option value="Online">Online</option>
                      </select>

                      <label>Payment Type</label>
                    </div>
                  </div>

                  {/* STATUS */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="status"
                        value={editForm.status}
                        onChange={(e) =>
                          handleEditChange("status", e.target.value)
                        }
                        required
                      >
                        <option value="1">Complete</option>

                        <option value="2">Pending</option>
                      </select>

                      <label>Payment Status</label>
                    </div>
                  </div>

                  {/* DESCRIPTION */}

                  <div className="col-12">
                    <div className="form-floating form-floating-outline">
                      <textarea
                        className="form-control"
                        name="description"
                        style={{
                          minHeight: "100px",
                        }}
                        value={editForm.description}
                        onChange={(e) =>
                          handleEditChange("description", e.target.value)
                        }
                      />

                      <label>Remark / Description</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer border-0 px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                  onClick={() => setError({})}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4 d-flex align-items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line" />
                      Update Expense
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
